/**
 * Turning a flat list of pages into the shape a site actually has.
 *
 * DERIVED, NEVER STORED. The server sends pages and the paths they publish at;
 * the tree is a grouping over that. Nothing to keep in sync, and it cannot show
 * a branch that no page is filed under — which is exactly the property that
 * makes it worth trusting as a check on the paths themselves.
 *
 * Its own module with no React: CRA's jest cannot resolve react-router-dom v7
 * or react-dnd, so anything sharing a file with a component that imports them
 * is untestable.
 */

export interface StructurePage {
  id: number;
  /** True for the page that IS this section — it sits ON the branch, not in it. */
  is_section_index?: boolean;
  title: string | null;
  slug: string | null;
  path_segments: string[];
  last_deployed_at: string | null;
  state: string | null;
  subject_name: string | null;
}

export interface StructureNode {
  /** Full path from the root, e.g. ["bikes", "trails"]. */
  path: string[];
  /** The last segment — what the row is labelled with. */
  name: string;
  children: StructureNode[];
  /** Pages filed directly AT this node, not below it. */
  pages: StructurePage[];
  /** The page that IS this section, when one exists. Never in `pages`. */
  index: StructurePage | null;
  /** Pages at this node and everywhere under it. */
  totalPages: number;
  /** How many of those have actually shipped. */
  deployedPages: number;
}

const key = (segments: string[]) => segments.join('/');

/**
 * Build the tree.
 *
 * Intermediate nodes are created even when nothing is filed at them — a page at
 * `a/b/c` implies branches `a` and `a/b`, and omitting them would draw `c`
 * hanging off the root, which is a different site shape from the real one.
 */
export function buildStructure(pages: StructurePage[]): StructureNode[] {
  const nodes = new Map<string, StructureNode>();
  const roots: StructureNode[] = [];

  const ensure = (path: string[]): StructureNode => {
    const k = key(path);
    const found = nodes.get(k);
    if (found) return found;
    const node: StructureNode = {
      path,
      name: path[path.length - 1],
      children: [],
      pages: [],
      index: null,
      totalPages: 0,
      deployedPages: 0,
    };
    nodes.set(k, node);
    if (path.length === 1) {
      roots.push(node);
    } else {
      ensure(path.slice(0, -1)).children.push(node);
    }
    return node;
  };

  // Pages filed at the site root have no node to hang off; they are returned
  // separately by `rootPages` rather than inventing an empty branch for them.
  for (const page of pages) {
    const segments = page.path_segments.filter(Boolean);
    if (!segments.length) continue;
    const node = ensure(segments);
    // A section index is the branch, not a leaf under it. Listing it among the
    // pages would draw the section twice — once as a row and once as a child of
    // itself — and make the count read one too high.
    if (page.is_section_index) node.index = page;
    else node.pages.push(page);
  }

  const roll = (node: StructureNode): void => {
    node.children.forEach(roll);
    // The index counts as a page of this branch — it is a real page that ships
    // — but it is stored apart so the tree can draw it on the branch row.
    const own = node.index ? [...node.pages, node.index] : node.pages;
    node.totalPages =
      own.length + node.children.reduce((sum, c) => sum + c.totalPages, 0);
    node.deployedPages =
      own.filter((p) => p.last_deployed_at).length +
      node.children.reduce((sum, c) => sum + c.deployedPages, 0);
    // Busiest branch first, then alphabetical, so the ordering is stable
    // between two loads that saw the same data.
    node.children.sort(
      (a, b) => b.totalPages - a.totalPages || a.name.localeCompare(b.name),
    );
    node.pages.sort((a, b) => (a.slug ?? '').localeCompare(b.slug ?? ''));
  };
  roots.forEach(roll);
  roots.sort((a, b) => b.totalPages - a.totalPages || a.name.localeCompare(b.name));
  return roots;
}

/** Pages published at the site root, which belong to no branch. */
export function rootPages(pages: StructurePage[]): StructurePage[] {
  return pages
    .filter((p) => p.path_segments.filter(Boolean).length === 0)
    .sort((a, b) => (a.slug ?? '').localeCompare(b.slug ?? ''));
}

/**
 * Branches that look like a typo of a sibling.
 *
 * THE FAILURE THIS SCREEN IS FOR. `types-of-ebikes` beside `type-of-ebikes`
 * reads as one branch in a list and is two different URLs. Nothing downstream
 * can tell them apart, and nobody notices until they read the deployed site.
 *
 * Deliberately narrow — an edit distance of 1 between siblings. A looser rule
 * flags legitimately similar names (`road-bikes` / `trail-bikes`), and a
 * warning that cries wolf is worse than none, because it teaches people to
 * dismiss the row without reading it.
 */
export function suspectSiblings(nodes: StructureNode[]): string[][] {
  const flagged: string[][] = [];
  const walk = (siblings: StructureNode[]) => {
    for (let i = 0; i < siblings.length; i++) {
      for (let j = i + 1; j < siblings.length; j++) {
        if (editDistanceAtMost(siblings[i].name, siblings[j].name, 1)) {
          flagged.push([key(siblings[i].path), key(siblings[j].path)]);
        }
      }
    }
    siblings.forEach((s) => walk(s.children));
  };
  walk(nodes);
  return flagged;
}

/** True when `a` and `b` are within `max` single-character edits. */
export function editDistanceAtMost(a: string, b: string, max: number): boolean {
  if (a === b) return false; // identical is not a typo, it is the same branch
  if (Math.abs(a.length - b.length) > max) return false;
  let edits = 0;
  let i = 0;
  let j = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i++;
      j++;
      continue;
    }
    if (++edits > max) return false;
    if (a.length > b.length) i++;
    else if (b.length > a.length) j++;
    else {
      i++;
      j++;
    }
  }
  edits += a.length - i + (b.length - j);
  return edits <= max;
}
